import { Link } from 'react-router-dom';
import { MapPin, ShieldCheck, Clock, BadgeCheck, Quote } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import StatsBand from '@/components/StatsBand';
import CTABand from '@/components/CTABand';
import { Reveal, Stagger, StaggerItem, Eyebrow } from '@/components/Reveal';
import { useLanguage } from '@/context/LanguageContext';

const WHY_ICONS = [MapPin, ShieldCheck, Clock, BadgeCheck];

export default function About() {
  const { t } = useLanguage();
  const story = t('about.story');
  const why = t('about.why.items');
  const testimonials = t('testimonials.items');

  return (
    <div className="pt-28" data-testid="about-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="about.eyebrow" title="about.title" align="center" />
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 lg:grid-cols-12 lg:px-8" data-testid="about-story">
        <Reveal className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-[2rem] border border-brand-line bg-gradient-to-br from-brand-blue/5 to-brand-sky/10 p-10 text-center">
            <img src={t('about.img')} alt="WiyakGPS logo" className="mx-auto h-44 w-44 rounded-full shadow-[0_30px_60px_-20px_rgba(0,82,255,0.5)]" data-testid="about-logo" />
            <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-brand-dim" dir="ltr">{t('about.badge')}</p>
            <p className="mt-2 font-display text-lg font-extrabold text-brand-ink">{t('hero.tagline')}</p>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>{t('about.eyebrow')}</Eyebrow></Reveal>
          {story.map((p, i) => (
            <Reveal key={i} delay={0.08 + i * 0.08}>
              <p className="mt-5 text-base leading-loose text-brand-steel sm:text-lg">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-5 px-5 py-10 md:grid-cols-2 lg:px-8" data-testid="about-vision-mission">
        {['vision', 'mission'].map((k, i) => (
          <Reveal key={k} delay={i * 0.1}>
            <div className="card-hover h-full rounded-[2rem] border border-brand-line bg-white p-9" data-testid={`about-${k}`}>
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-brand-blue" dir="ltr">0{i + 1}</span>
              <h3 className="mt-3 font-display text-2xl font-extrabold text-brand-ink">{t(`about.${k}.t`)}</h3>
              <p className="mt-3 leading-relaxed text-brand-steel">{t(`about.${k}.d`)}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-y border-brand-line bg-white py-20" data-testid="about-why">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow="about.eyebrow" title="about.why.t" align="center" />
          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" gap={0.09}>
            {why.map((w, i) => {
              const Icon = WHY_ICONS[i] || BadgeCheck;
              return (
                <StaggerItem key={w.t}>
                  <div className="card-hover h-full rounded-2xl border border-brand-line bg-brand-paper p-6" data-testid={`why-card-${i}`}>
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-teal/10 text-brand-teal border border-brand-teal/20"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 font-display text-base font-bold text-brand-ink">{w.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-brand-steel">{w.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <StatsBand />

      <section className="py-20" data-testid="about-testimonials">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow="testimonials.eyebrow" title="testimonials.title" align="center" />
          <Stagger className="mt-12 grid gap-4 md:grid-cols-3" gap={0.1}>
            {testimonials.map((item) => (
              <StaggerItem key={item.n}>
                <div className="card-hover flex h-full flex-col rounded-2xl border border-brand-line bg-white p-7">
                  <Quote className="h-6 w-6 text-brand-blue" />
                  <p className="mt-4 flex-1 text-base leading-relaxed text-brand-ink">“{item.q}”</p>
                  <div className="mt-6 border-t border-brand-line pt-4">
                    <p className="text-sm font-extrabold text-brand-ink">{item.n}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-brand-dim">{item.r}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15} className="mt-10 text-center">
            <Link to={t('about.ctaLink')} data-testid="about-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-brand-blueHover">{t('ctaBand.cta2')}</Link>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
